/**
 * This class defines three methods for Menu Board app to request products and account settings from the Player.
 */

import Bridge from './bridge/bridge';
import { Action, Service } from './enums';

/**
 * Menu Board API is a part of the Player SDK used for fetching menu board's app updated products.
 *
 * Useful commands:
 */
export default class MenuBoard {
  /** @internal */
  constructor(private bridge: Bridge) { }

  /**
   * Get products by their IDs
   * @param productIds Array of product IDs to fetch
   * @returns Promise resolving to the products data
   */
  public getProductsByIds<T>(productIds: string[]): Promise<T> {
    const payload = { productIds };
    return this.bridge.send(Service.MenuBoard, Action.GetProductsByIds, payload);
  }

  /**
   * Get filtered products for a specific asset
   * @param assetId The asset ID to get products for
   * @param timedLayoutActive The timed layout status
   * @param locationId The location ID to get products for that location
   * @returns Promise resolving to the filtered products
   */
  public getFilteredProducts<T>(assetId: string, timedLayoutActive: string, locationId: string): Promise<T> {
    const payload = { assetId, timedLayoutActive, locationId };
    return this.bridge.send(Service.MenuBoard, Action.GetFilteredProducts, payload);
  }

  /**
   * Get account settings for a specific account
   * @param accountId The account ID to get settings for
   * @returns Promise resolving to the account settings
   */
  public getAccountSettings<T>(accountId: string): Promise<T> {
    const payload = { accountId };
    return this.bridge.send(Service.MenuBoard, Action.GetAccountSettings, payload);
  }

  /**
   * Get preload metadata for the current and scheduled layouts of a menu-board asset
   * @param assetId - The asset ID to warm on the host
   * @param timedLayoutActive - The currently active layout ID
   * @param locationId - Optional location filter for filtered products
   * @returns Promise resolving to the scheduled preload manifest
   */
  public getScheduledPreload<T>(
    assetId: string,
    timedLayoutActive: string | null,
    locationId: string | null
  ): Promise<T> {
    const payload = { assetId, timedLayoutActive, locationId };
    return this.bridge.send(Service.MenuBoard, Action.GetScheduledPreload, payload);
  }
}
